import React, { useMemo, useState, useEffect } from 'react'
import { Tags } from 'lucide-react'
import type { ContactTag } from '../../types/conversation.types'

/** Quantidade de tags exibidas por defeito (mais frequentes na lista atual). */
export const DEFAULT_TAG_FILTER_TOP_VISIBLE = 8

/** Itens da lista atual (conversas ou clientes) com tags para contagem de frequência. */
export type TagUsageSourceItem = { tags?: Array<{ id: number }> }

function countTagUsage(items: TagUsageSourceItem[]): Map<number, number> {
  const map = new Map<number, number>()
  for (const item of items) {
    for (const t of item.tags ?? []) {
      map.set(t.id, (map.get(t.id) ?? 0) + 1)
    }
  }
  return map
}

export interface TagFilterPanelProps {
  availableTags: ContactTag[]
  selectedTagIds: number[]
  onChangeSelectedTagIds: React.Dispatch<React.SetStateAction<number[]>>
  tagMatch: 'any' | 'all'
  onChangeTagMatch: (mode: 'any' | 'all') => void
  /** Conversas ou clientes carregados — usado para ordenar tags por frequência na lista atual. */
  usageItems: TagUsageSourceItem[]
  /** Nome único para os radios (evitar colisão entre páginas). */
  radioGroupName: string
  /** Quantidade de tags “rápidas” antes do botão expandir. */
  topVisibleCount?: number
  /** Classes do botão Limpar (ex.: tema azul ou âmbar). */
  clearButtonClassName?: string
}

/**
 * Filtro por tags com chips ordenados por uso na lista atual; mostra as mais frequentes
 * e permite expandir para ver e selecionar as restantes.
 */
export const TagFilterPanel: React.FC<TagFilterPanelProps> = ({
  availableTags,
  selectedTagIds,
  onChangeSelectedTagIds,
  tagMatch,
  onChangeTagMatch,
  usageItems,
  radioGroupName,
  topVisibleCount = DEFAULT_TAG_FILTER_TOP_VISIBLE,
  clearButtonClassName = 'text-blue-600 hover:underline'
}) => {
  const [showAllTags, setShowAllTags] = useState(false)

  const usageById = useMemo(() => countTagUsage(usageItems), [usageItems])

  const sortedTags = useMemo(() => {
    return [...availableTags].sort((a, b) => {
      const ca = usageById.get(a.id) ?? 0
      const cb = usageById.get(b.id) ?? 0
      if (cb !== ca) return cb - ca
      return a.name.localeCompare(b.name, 'pt')
    })
  }, [availableTags, usageById])

  const tagsToShow = useMemo(() => {
    if (showAllTags) {
      return sortedTags
    }
    const topSlice = sortedTags.slice(0, topVisibleCount)
    const topIds = new Set(topSlice.map(t => t.id))
    const extraSelected = sortedTags.filter(
      t => selectedTagIds.includes(t.id) && !topIds.has(t.id)
    )
    return [...topSlice, ...extraSelected]
  }, [sortedTags, topVisibleCount, selectedTagIds, showAllTags])

  const hiddenCount = useMemo(() => {
    const shownIds = new Set(tagsToShow.map(t => t.id))
    return sortedTags.filter(t => !shownIds.has(t.id)).length
  }, [sortedTags, tagsToShow])

  useEffect(() => {
    const valid = new Set(availableTags.map(t => t.id))
    onChangeSelectedTagIds(prev => {
      const next = prev.filter(id => valid.has(id))
      return next.length === prev.length ? prev : next
    })
  }, [availableTags, onChangeSelectedTagIds])

  if (availableTags.length === 0) {
    return (
      <div className="border border-gray-200 rounded-lg p-3 bg-gray-50/80">
        <div className="flex items-center gap-2 mb-2">
          <Tags className="w-4 h-4 text-gray-500 shrink-0" />
          <span className="text-sm font-medium text-gray-700">Filtrar por tags</span>
        </div>
        <p className="text-xs text-gray-500">Nenhuma tag cadastrada.</p>
      </div>
    )
  }

  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50/80">
      <div className="flex items-center gap-2 mb-2">
        <Tags className="w-4 h-4 text-gray-500 shrink-0" />
        <span className="text-sm font-medium text-gray-700">Filtrar por tags</span>
        {selectedTagIds.length > 0 && (
          <button
            type="button"
            onClick={() => onChangeSelectedTagIds([])}
            className={`text-xs ml-auto ${clearButtonClassName}`}
          >
            Limpar
          </button>
        )}
      </div>
      <p className="text-[11px] text-gray-500 mb-2">
        Ordenadas por uso na lista atual; as primeiras são as mais frequentes.
      </p>
      <div
        className={`flex flex-wrap gap-1.5 mb-2 ${showAllTags ? 'max-h-40 overflow-y-auto' : 'max-h-28 overflow-y-auto'}`}
      >
        {tagsToShow.map(tag => {
          const active = selectedTagIds.includes(tag.id)
          const uses = usageById.get(tag.id) ?? 0
          return (
            <button
              key={tag.id}
              type="button"
              onClick={() =>
                onChangeSelectedTagIds(prev =>
                  active ? prev.filter(id => id !== tag.id) : [...prev, tag.id]
                )
              }
              className={`text-xs px-2 py-1 rounded-full border transition-colors truncate max-w-[140px] ${
                active ? 'ring-2 ring-offset-1' : 'opacity-90 hover:opacity-100'
              }`}
              style={{
                borderColor: tag.color,
                backgroundColor: active ? `${tag.color}22` : 'white'
              }}
              title={uses > 0 ? `${tag.name} (${uses} nesta lista)` : tag.name}
            >
              {tag.name}
            </button>
          )
        })}
      </div>
      {(showAllTags || hiddenCount > 0) && (
        <button
          type="button"
          onClick={() => setShowAllTags(v => !v)}
          className="text-xs text-gray-600 hover:text-gray-900 underline mb-2"
        >
          {showAllTags
            ? 'Mostrar só as mais usadas'
            : `Ver todas as tags (${hiddenCount} restantes)`}
        </button>
      )}
      {selectedTagIds.length > 1 && (
        <div className="flex items-center gap-3 text-xs text-gray-600">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="radio"
              name={radioGroupName}
              checked={tagMatch === 'any'}
              onChange={() => onChangeTagMatch('any')}
            />
            Qualquer tag
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="radio"
              name={radioGroupName}
              checked={tagMatch === 'all'}
              onChange={() => onChangeTagMatch('all')}
            />
            Todas as tags
          </label>
        </div>
      )}
    </div>
  )
}
